<script setup lang="ts">
import { computed, onMounted, onUnmounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthPopupRecovery } from '@/composables/useAuthPopupRecovery'
import { resolveChatContext } from '@/runtime/chatContext'
import { AUTH_FAILURE_REDIRECT_EVENT, authSession } from '@/auth/session'

const props = withDefaults(defineProps<{
  autoStartPopupOnShow?: boolean
  enabled?: boolean
}>(), {
  autoStartPopupOnShow: false,
  enabled: true,
})

const route = useRoute()
const router = useRouter()
const botName = computed(() => typeof route.params.botName === 'string' ? route.params.botName : null)
const authSurface = computed<'embedded' | 'full'>(() =>
  route.meta.authSurface === 'embedded' ? 'embedded' : 'full',
)
const context = computed(() => {
  return resolveChatContext({ botId: botName.value, authState: authSession })
})
const accessRequiresAuth = computed(() => context.value.access === 'requires_auth')

const authPopupRecovery = useAuthPopupRecovery()
const {
  loginInProgress,
  popupBlocked,
  loginError,
  startLoginPopup,
  retryLoginPopup,
  markLoginRequired,
} = authPopupRecovery

const showAuthGate = computed(() =>
  props.enabled && ((!authSession.isAuthenticated && accessRequiresAuth.value) || authPopupRecovery.loginRequired.value),
)
const gateTitle = computed(() =>
  accessRequiresAuth.value ? 'Sign in required' : 'Session expired',
)
const gateDescription = computed(() =>
  accessRequiresAuth.value
    ? 'Sign in with UMD CAS to continue.'
    : 'Sign in again to continue without leaving this page.',
)
const standaloneChatHref = computed(() => {
  if (botName.value) {
    return router.resolve({ name: 'bot-name', params: { botName: botName.value } }).href
  }
  return router.resolve({ name: 'landing' }).href
})

function handleAuthorizationFailure() {
  if (authSurface.value === 'embedded') {
    markLoginRequired()
  }
}

function handleLoginClick() {
  startLoginPopup(route.fullPath)
}

function handleRetryLoginClick() {
  retryLoginPopup(route.fullPath)
}

onMounted(() => {
  window.addEventListener(AUTH_FAILURE_REDIRECT_EVENT, handleAuthorizationFailure)
})

onUnmounted(() => {
  window.removeEventListener(AUTH_FAILURE_REDIRECT_EVENT, handleAuthorizationFailure)
})

watch(
  () => showAuthGate.value,
  (isVisible, wasVisible) => {
    if (!props.autoStartPopupOnShow) return
    if (authSurface.value !== 'embedded') return
    if (!isVisible || wasVisible) return
    if (loginInProgress.value) return
    handleLoginClick()
  },
)
</script>

<template>
  <Teleport to="body">
    <div
      v-if="showAuthGate"
      class="fixed inset-0 z-auth-overlay pointer-events-auto flex items-center justify-center bg-slate-950/30 px-6"
      :class="{ 'bg-slate-950/40': authSurface === 'full' }"
    >
      <div class="w-full max-w-md rounded-3xl border border-sky-200 bg-sky-50/95 p-7 text-center shadow-lg">
        <p class="text-xs font-semibold uppercase tracking-[0.3em] text-sky-700">
          Authentication
        </p>
        <h1 class="mt-3 text-2xl font-semibold text-slate-950">{{ gateTitle }}</h1>
        <p class="mt-3 text-sm leading-6 text-slate-700">{{ gateDescription }}</p>
        <p
          v-if="loginError"
          class="mt-3 text-xs text-red-700"
        >
          {{ loginError }}
        </p>
        <div class="mt-5 flex flex-wrap justify-center gap-2">
          <button
            type="button"
            class="inline-flex items-center justify-center rounded-full bg-slate-950 px-5 py-2.5 text-sm font-medium text-white transition hover:bg-slate-800"
            :disabled="loginInProgress"
            @click="handleLoginClick"
          >
            {{ loginInProgress ? 'Waiting for sign-in…' : 'Sign in with popup' }}
          </button>
          <button
            v-if="popupBlocked"
            type="button"
            class="inline-flex items-center justify-center rounded-full border border-slate-300 bg-white px-5 py-2.5 text-sm font-medium text-slate-900 transition hover:bg-slate-100"
            :disabled="loginInProgress"
            @click="handleRetryLoginClick"
          >
            Retry popup
          </button>
          <a
            :href="standaloneChatHref"
            target="_blank"
            rel="noopener"
            class="inline-flex items-center justify-center rounded-full border border-slate-300 bg-white px-5 py-2.5 text-sm font-medium text-slate-900 transition hover:bg-slate-100"
          >
            Open this chat in a new window/tab
          </a>
        </div>
      </div>
    </div>
  </Teleport>
</template>
